import type { ForestOwnerIdentityOutput } from '@/schemas/register.forestowner'
import {
  type ContactPreferenceModel,
  ContactPreferenceTypeCode,
} from '@/types/ForestOwners/ContactPreference'
import type {
  ChangeForestOwnerToOrganizationRequest,
  CreatedForestOwnerResourceModel,
  CreateForestOwnerRequest,
  CreateForestOwnerResponse,
  ForestOwnerModel,
  ForestOwnerSummaryModel,
  GetForestOwnersParams,
  PostForestOwnerActivateRequest,
  PostForestOwnerSubscriptionRequest,
  SetForestOwnerContactPreferencesRequest,
  UpdateForestOwnerRemarkRequest,
  UpdateForestOwnerRequest,
  UpdateForestOwnerStatusRequest,
  UpdateMagazineSubscriptionRequest,
  UploadPowerOfAttorneyModel,
} from '@/types/ForestOwners/ForestOwner'
import type {
  VerifyPropertyOwnershipParams,
  VerifyPropertyOwnershipResponse,
} from '@/types/ForestOwners/Property'
import {
  buildIdentificationNumber,
  buildOperatingOrganization,
  mapOrganizationPayload,
  mapPrivatePersonPayload,
  mapSameiePayload,
} from '@/utils/forest-owner-registration-mappers'
import { parseToPhoneNumberModel } from '@/utils/phone'
import { getCurrentTenant } from '@/utils/tenant-utils'
import {
  ApiClient,
  ApiError,
  apiDelete,
  apiGet,
  apiGetWithPagedResult,
  apiPatch,
  apiPost,
  apiPut,
  type PagedResult,
} from '../ApiClient'
import { getCompany } from '../CompanyService'

export async function getForestOwners(
  params: GetForestOwnersParams,
): Promise<PagedResult<ForestOwnerSummaryModel>> {
  const queryParams: Record<string, string | boolean> = {}
  if (params.searchTerm) queryParams.searchTerm = params.searchTerm
  if (params.sort) queryParams.sort = params.sort
  if (params.onlyShareholders) queryParams.onlyShareholders = true
  if (params.forestOwnerType) queryParams.forestOwnerType = String(params.forestOwnerType)

  return apiGetWithPagedResult<ForestOwnerSummaryModel>(
    '/forestowners',
    {
      tenantId: params.tenantId,
      pageSize: params.pageSize ?? 50,
      continuationToken: params.continuationToken,
    },
    Object.keys(queryParams).length > 0 ? queryParams : undefined,
  )
}
export async function getForestOwner(id: string): Promise<ForestOwnerModel> {
  return apiGet<ForestOwnerModel>(`/forestowners/${encodeURIComponent(id)}`)
}

export async function verifyPropertyOwnership(
  params: VerifyPropertyOwnershipParams,
): Promise<VerifyPropertyOwnershipResponse> {
  return apiGet<VerifyPropertyOwnershipResponse>(
    `/forestowners/${encodeURIComponent(params.forestOwnerId)}/ownerships/verify`,
    {
      params: {
        municipalityNumber: params.municipalityNumber,
        farmNumber: params.farmNumber,
        holdingNumber: params.holdingNumber,
      },
      transforms: [
        [
          (root) => root.owners?.[0]?.organizationNumber,
          (number) => (typeof number === 'string' && number.length === 9 ? number : undefined),
        ],
      ],
    },
  )
}

export async function updateForestOwner(
  id: string,
  forestOwnerData: Partial<UpdateForestOwnerRequest>,
  etag?: string | null,
): Promise<ForestOwnerModel> {
  return apiPatch<ForestOwnerModel>(`/forestowners/${encodeURIComponent(id)}`, forestOwnerData, {
    etag,
  })
}

export async function changeForestOwnerToOrganization(
  id: string,
  forestOwnerData: ChangeForestOwnerToOrganizationRequest,
  etag: string | undefined,
): Promise<ForestOwnerModel> {
  if (!etag) {
    throw new Error('Cannot change forest owner type without an ETag from the current resource')
  }

  return apiPut<ForestOwnerModel>(`/forestowners/${encodeURIComponent(id)}`, forestOwnerData, {
    etag,
  })
}

export async function updateForestOwnerRegistrationData(
  forestOwnerId: string,
  formData: ForestOwnerIdentityOutput,
  etag?: string | null,
): Promise<ForestOwnerModel> {
  const ownerType = formData.step0.forestOwnerType
  const stepData =
    ownerType === 'private' && 'step1Person' in formData
      ? formData.step1Person
      : 'step1Organization' in formData
        ? formData.step1Organization
        : undefined
  if (!stepData)
    throw new Error(`Missing registration identity for forest owner type: ${ownerType}`)

  const apiPhone = parseToPhoneNumberModel(stepData.phone, true)

  // For Sameie (shared) type, use organizationName as firstName with null lastName
  const nameData =
    (ownerType === 'organization' || ownerType === 'shared') &&
    'organizationName' in stepData &&
    stepData.organizationName.trim() !== ''
      ? { firstName: stepData.organizationName.trim(), lastName: null }
      : { firstName: stepData.firstName, lastName: stepData.lastName }

  const updatePayload: Partial<UpdateForestOwnerRequest> = {
    name: nameData,
    email: stepData.email?.trim() ? stepData.email.trim() : undefined,
    address: {
      address: {
        streetName: stepData.address,
        houseNumber: stepData.houseNumber,
        city: stepData.city,
        postalCode: stepData.postcode,
        country: stepData.country,
      },
      careOf:
        stepData.isAddressOwner && stepData.addressOwnerName
          ? stepData.addressOwnerName
          : undefined,
    },
    mobilePhone: apiPhone,
    landlinePhone: apiPhone,
    bankAccountNumber: stepData.accountNumber?.trim() ? stepData.accountNumber.trim() : null,
  }

  if (ownerType === 'private') {
    const { identificationNumber, birthDate } = buildIdentificationNumber(
      stepData.birthDate,
      stepData.ssn,
    )

    if (identificationNumber) {
      updatePayload.identificationNumber = identificationNumber
    }

    if (birthDate) {
      updatePayload.birthDate = birthDate
    }
  }

  if (ownerType === 'organization' || ownerType === 'shared') {
    const { birthDate: contactBirthDate } = buildIdentificationNumber(
      stepData.birthDate,
      stepData.ssn,
    )
    updatePayload.contactPerson = {
      name: { firstName: stepData.firstName, lastName: stepData.lastName },
      email: stepData.email?.trim() || undefined,
      phoneNumber: apiPhone ? { mobile: apiPhone } : undefined,
      socialSecurityNumber: stepData.ssn?.trim() || undefined,
      birthDate: contactBirthDate as string,
    }
    const operatingOrganization = buildOperatingOrganization(
      stepData,
      'organizationName' in stepData ? stepData.organizationName : undefined,
    )
    if (operatingOrganization) {
      updatePayload.operatingOrganization = operatingOrganization
    }
  }

  return apiPatch<ForestOwnerModel>(
    `/forestowners/${encodeURIComponent(forestOwnerId)}`,
    updatePayload,
    { etag },
  )
}

export async function updateForestOwnerRemark(
  id: string,
  note: string,
  etag?: string | null,
): Promise<void> {
  const remark: UpdateForestOwnerRemarkRequest = { remark: note }
  await apiPut(`/forestowners/${encodeURIComponent(id)}/remark`, remark, { etag })
}

export async function setForestOwnerContactPreferences(
  id: string,
  preferences: ContactPreferenceModel[],
  etag?: string | null,
): Promise<void> {
  const request: SetForestOwnerContactPreferencesRequest = { contactPreferences: preferences }
  await apiPut(`/forestowners/${encodeURIComponent(id)}/contact-preferences`, request, { etag })
}

export async function addForestOwnerSubscription(
  forestOwnerId: string,
  subscription: PostForestOwnerSubscriptionRequest,
): Promise<CreatedForestOwnerResourceModel> {
  return apiPost<CreatedForestOwnerResourceModel>(
    `/forestowners/${encodeURIComponent(forestOwnerId)}/subscriptions`,
    subscription,
  )
}

export async function updateForestOwnerSubscription(
  forestOwnerId: string,
  subscriptionId: string,
  subscription: UpdateMagazineSubscriptionRequest,
  etag?: string | null,
): Promise<void> {
  await apiPatch(
    `/forestowners/${encodeURIComponent(forestOwnerId)}/subscriptions/${encodeURIComponent(subscriptionId)}`,
    subscription,
    { etag },
  )
}

export async function removeForestOwnerSubscription(
  forestOwnerId: string,
  subscriptionId: string,
  etag?: string | null,
): Promise<void> {
  await apiDelete(
    `/forestowners/${encodeURIComponent(forestOwnerId)}/subscriptions/${encodeURIComponent(subscriptionId)}`,
    {
      headers: etag ? { 'If-Match': etag } : undefined,
    },
  )
}

export async function activateForestOwner(
  forestOwnerId: string,
  wasWoodSentBefore: boolean,
): Promise<void> {
  const request: PostForestOwnerActivateRequest = { wasWoodSentBefore }
  await apiPost(`/forestowners/${encodeURIComponent(forestOwnerId)}/activate`, request)
}

export async function updateForestOwnerStatus(
  id: string,
  request: UpdateForestOwnerStatusRequest,
  etag?: string | null,
): Promise<void> {
  await apiPut(`/forestowners/${encodeURIComponent(id)}/status`, request, { etag })
}

export async function uploadPowerOfAttorney(
  forestOwnerId: string,
  file: File,
): Promise<UploadPowerOfAttorneyModel> {
  const formData = new FormData()
  formData.append('file', file)

  return apiPost<UploadPowerOfAttorneyModel>(
    `/forestowners/${encodeURIComponent(forestOwnerId)}/power-of-attorney`,
    formData,
  )
}

export async function downloadPowerOfAttorney(
  forestOwnerId: string,
): Promise<{ blob: Blob; fileName: string }> {
  const response = await ApiClient.get<Blob>(
    `/forestowners/${encodeURIComponent(forestOwnerId)}/power-of-attorney`,
    {
      responseType: 'blob',
    },
  )

  if (!response.ok) {
    throw new ApiError(response.status, response.statusText, response.data)
  }

  // Extract filename from Content-Disposition header
  const contentDisposition = response.headers.get('content-disposition')
  let fileName = `power-of-attorney-${forestOwnerId}.pdf`

  const fileNameMatch = contentDisposition?.match(/filename[^;=\n]*=((['"]).*?\2|[^;\n]*)/)
  if (fileNameMatch?.[1]) {
    fileName = fileNameMatch[1].replace(/['"]/g, '')
  }

  return { blob: response.data, fileName }
}

export async function deletePowerOfAttorney(
  forestOwnerId: string,
  etag?: string | null,
): Promise<void> {
  await apiDelete(`/forestowners/${encodeURIComponent(forestOwnerId)}/power-of-attorney`, {
    headers: etag ? { 'If-Match': etag } : undefined,
  })
}

function isContactPreferenceModelArray(value: unknown): value is ContactPreferenceModel[] {
  return (
    Array.isArray(value) &&
    value.every((item) => {
      if (!item || typeof item !== 'object') {
        return false
      }

      const preference = item as Partial<ContactPreferenceModel>
      return (
        typeof preference.typeCode === 'number' &&
        typeof preference.typeName === 'string' &&
        typeof preference.value === 'string'
      )
    })
  )
}

function mapFormPreferencesToResponsePreferences(
  responsePreferences: ContactPreferenceModel[],
  formPreferences: {
    general?: string[]
    documents?: string
    invoice?: string
  },
): ContactPreferenceModel[] {
  return responsePreferences.map((pref) => {
    // Special handling for typeCode 9 (General info) when Email and/or SMS are selected
    if (
      pref.typeCode === ContactPreferenceTypeCode.GENERELL_INFORMASJON &&
      formPreferences.general
    ) {
      const hasEmail = formPreferences.general.includes('email')
      const hasSms = formPreferences.general.includes('sms')

      // Combine Email and SMS into a single flagged value
      // Email = 2, SMS = 4, Email|SMS = 6
      if (hasEmail && hasSms) {
        return {
          typeCode: pref.typeCode,
          typeName: pref.typeName,
          value: 6,
        }
      }
      // Single selection
      else if (hasEmail) {
        return {
          typeCode: pref.typeCode,
          typeName: pref.typeName,
          value: 'Email' as const,
        }
      } else if (hasSms) {
        return {
          typeCode: pref.typeCode,
          typeName: pref.typeName,
          value: 4,
        }
      }
      // No selection - keep default (None)
    }

    // Handle other typeCodes normally
    let value: 'None' | 'Paper' | 'Email' | 'Sms' | 'Ehf' | number = pref.value

    if (pref.typeCode === ContactPreferenceTypeCode.AVTALEDOKUMENTER && formPreferences.documents) {
      // Documents (TypeCode 8)
      if (formPreferences.documents === 'email') {
        value = 'Email'
      } else if (formPreferences.documents === 'post') {
        value = 'Paper'
      }
    } else if (
      pref.typeCode === ContactPreferenceTypeCode.VIRKESAVREGNING &&
      formPreferences.invoice
    ) {
      // Invoice (TypeCode 1)
      if (formPreferences.invoice === 'email') {
        value = 'Email'
      } else if (formPreferences.invoice === 'post') {
        value = 'Paper'
      }
    }

    return {
      typeCode: pref.typeCode,
      typeName: pref.typeName,
      value,
    }
  })
}

export async function sendForestOwnerFollowUp(forestOwnerId: string): Promise<void> {
  await apiPost(`/forestowners/${encodeURIComponent(forestOwnerId)}/followup`, {})
}

export async function sendShareholderRegistrationNotification(
  forestOwnerId: string,
): Promise<void> {
  await apiPost(`/forestowners/${encodeURIComponent(forestOwnerId)}/notify-registration`, {})
}

export async function createForestOwner(
  formData: ForestOwnerIdentityOutput,
): Promise<CreateForestOwnerResponse> {
  const tenantId = getCurrentTenant()
  const ownerType = formData.step0.forestOwnerType
  const stepData =
    ownerType === 'private' && 'step1Person' in formData
      ? formData.step1Person
      : 'step1Organization' in formData
        ? formData.step1Organization
        : undefined
  if (!stepData)
    throw new Error(`Missing registration identity for forest owner type: ${ownerType}`)

  let organizationType: { code: string; description: string } | undefined
  const organizationNumber = stepData.selectedOrganization?.orgNumber ?? ''

  if (stepData.selectedOrganization?.orgNumber) {
    try {
      const company = await getCompany(stepData.selectedOrganization.orgNumber)
      if (company.organizationType) {
        organizationType = {
          code: company.organizationType.code,
          description: company.organizationType.description,
        }
      }
    } catch (error) {
      console.warn(
        `Failed to fetch organization type for ${stepData.selectedOrganization.orgNumber}:`,
        error,
      )
    }
  }

  let requestData: CreateForestOwnerRequest

  if (ownerType === 'private' && 'step1Person' in formData) {
    requestData = mapPrivatePersonPayload(formData, tenantId)
  } else if (ownerType === 'organization' && 'step1Organization' in formData) {
    requestData = mapOrganizationPayload(formData, tenantId, organizationType, organizationNumber)
  } else if (ownerType === 'shared' && 'step1Organization' in formData) {
    requestData = mapSameiePayload(formData, tenantId, organizationType, organizationNumber)
  } else {
    throw new Error(`Unknown forest owner type: ${ownerType}`)
  }

  const response = await apiPost<CreateForestOwnerResponse>('/forestowners', requestData)

  if (isContactPreferenceModelArray(response.contactPreferences)) {
    const contactPrefs = response.contactPreferences

    const hasFormPreferences = [
      Array.isArray(stepData.contactPreferenceGeneral) &&
        stepData.contactPreferenceGeneral.length > 0,
      typeof stepData.contactPreferenceDocuments === 'string' &&
        stepData.contactPreferenceDocuments.length > 0,
      typeof stepData.contactPreferenceInvoice === 'string' &&
        stepData.contactPreferenceInvoice.length > 0,
    ].some(Boolean)

    if (hasFormPreferences) {
      try {
        const mappedPreferences = mapFormPreferencesToResponsePreferences(contactPrefs, {
          general: stepData.contactPreferenceGeneral,
          documents: stepData.contactPreferenceDocuments,
          invoice: stepData.contactPreferenceInvoice,
        })

        await setForestOwnerContactPreferences(response.id, mappedPreferences)
      } catch (error) {
        console.warn('Failed to set contact preferences during registration:', error)
      }
    }
  }

  return response
}
